import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import qs from 'query-string';

import getEnvConfig from 'utils/config/env';

import { PaginatedResponse } from '../../../interfaces/PaginatedResponse';
import { ChatFilters, ChatProps } from '../interface/chats.interface';
import { AnalyticsFiltersProps } from '../../../../analytics/features/AnalyticsFilters/service/filters.interface';

export const chatsApi = createApi({
    reducerPath: 'chats',
    baseQuery: fetchBaseQuery({
        baseUrl: getEnvConfig().env.API_URL,
        prepareHeaders: headers => {
            const token = localStorage.getItem('accessToken');
            headers.append('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    tagTypes: ['Chats'],
    endpoints: builder => ({
        readChats: builder.query<
            PaginatedResponse<ChatProps>,
            Partial<ChatFilters | AnalyticsFiltersProps>
        >({
            query: params => {
                const query = qs.stringify(params, {
                    skipNull: true,
                    skipEmptyString: true,
                    arrayFormat: 'bracket',
                });
                return { url: `/user/chats?${query}` };
            },
            transformResponse: (
                response: unknown,
                meta
            ): PaginatedResponse<ChatProps> => {
                const res = response as { data: ChatProps[] };
                return {
                    items: res.data || [],
                    total:
                        Number(meta?.response?.headers?.get('Items-count')) ||
                        0,
                };
            },
            providesTags: ['Chats'],
        }),

        readChatTags: builder.query<
            Array<{ id: number; user_id: number; name: string }>,
            void
        >({
            query: () => ({
                url: '/chats/tags',
                method: 'GET',
            }),
            transformResponse: (data: {
                data: Array<{ id: number; user_id: number; name: string }>;
            }) => {
                return data.data;
            },
            providesTags: ['Chats'],
        }),

        createChatTags: builder.mutation<
            void,
            { tags: string[]; community_id: number }
        >({
            query: ({ community_id, tags }) => ({
                url: '/chat-tags/attach',
                method: 'POST',
                body: {
                    tags,
                    community_id,
                },
            }),
            invalidatesTags: ['Chats'],
        }),
    }),
});

export const {
    useReadChatsQuery,
    useReadChatTagsQuery,
    useLazyReadChatTagsQuery,
    useCreateChatTagsMutation,
    useLazyReadChatsQuery,
} = chatsApi;
